import { useEffect, useState } from 'react';
import { useAdmin } from '../../components/AdminLayout';
import { api } from '../../lib/api';
import s from './AdminDashboard.module.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdminStatsExtended {
  tenantCount: number;
  userCount: number;
  domainCount: number;
  reportsToday: number;
  reportsThisWeek: number;
  rejectedThisWeek: number;
  tenantsApproachingLimit: {
    id: string;
    name: string;
    plan: string;
    usage: number;
    limit: number;
  }[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AdminDashboard() {
  const { secret } = useAdmin();
  const [stats, setStats] = useState<AdminStatsExtended | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.admin
      .stats(secret)
      .then((data) => {
        if (!cancelled) {
          // The API may return a flat AdminStats or an extended shape.
          // Normalize to our extended type with sensible defaults.
          const ext: AdminStatsExtended = {
            tenantCount: data.tenantCount ?? 0,
            userCount: data.userCount ?? 0,
            domainCount: data.domainCount ?? 0,
            reportsToday: (data as any).reportsToday ?? data.activeLastDay ?? 0,
            reportsThisWeek: (data as any).reportsThisWeek ?? data.reportCount ?? 0,
            rejectedThisWeek: (data as any).rejectedThisWeek ?? 0,
            tenantsApproachingLimit: (data as any).tenantsApproachingLimit ?? [],
          };
          setStats(ext);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load stats');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [secret]);

  if (loading) {
    return <div className={s.loading}>Loading dashboard&hellip;</div>;
  }

  if (error) {
    return <div className={s.error}>{error}</div>;
  }

  if (!stats) return null;

  const kpis = [
    { label: 'Total Tenants',     value: stats.tenantCount },
    { label: 'Total Users',       value: stats.userCount },
    { label: 'Total Domains',     value: stats.domainCount },
    { label: 'Reports Today',     value: stats.reportsToday },
    { label: 'Reports This Week', value: stats.reportsThisWeek },
    { label: 'Rejected This Week', value: stats.rejectedThisWeek },
  ];

  return (
    <div className={s.page}>
      <h1 className={s.heading}>System Overview</h1>

      {/* KPI cards */}
      <div className={s.kpiGrid}>
        {kpis.map((kpi) => (
          <div key={kpi.label} className={s.kpiCard}>
            <div className={s.kpiLabel}>{kpi.label}</div>
            <div className={s.kpiValue}>{kpi.value.toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Tenants approaching limit */}
      <div className={s.section}>
        <h2 className={s.sectionTitle}>Tenants Approaching Limit</h2>
        {stats.tenantsApproachingLimit.length === 0 ? (
          <div className={s.empty}>No tenants are near their usage limits.</div>
        ) : (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Plan</th>
                  <th>Usage</th>
                  <th>Limit</th>
                  <th>Utilization</th>
                </tr>
              </thead>
              <tbody>
                {stats.tenantsApproachingLimit.map((t) => {
                  const pct = t.limit > 0 ? Math.round((t.usage / t.limit) * 100) : 0;
                  const barClass = pct >= 90 ? s.usageDanger : s.usageWarning;
                  return (
                    <tr key={t.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{t.name}</td>
                      <td>{t.plan}</td>
                      <td>{t.usage.toLocaleString()}</td>
                      <td>{t.limit.toLocaleString()}</td>
                      <td>
                        <div className={s.usageBar}>
                          <div
                            className={`${s.usageFill} ${barClass}`}
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <div className={s.usagePercent}>{pct}%</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
